import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import i18n from 'i18next';
import moment from 'moment';

import { PAGE_NAMES } from '../../pages.js';

import SessionsPrivacy from './SessionsPrivacy';
import PolarClubCommunication from './PolarClubCommunication';
import Consent from './Consent';
import Telemetry from './Telemetry';
import CancelSaveButton from '../CancelSaveButton';
import {AGE_CONSENT_META_DATA_BIRTHDAY, AGE_CONSENT_TYPE, AGE_CONSENT_FULL_AGE_LIMIT} from '../../../common';

import { getSettings, updateSettings, setCurrentView, invalidateAllTrainingShareLinks } from '../../actions/index';

import './PrivacyView.less';

export class PrivacyView extends React.Component {
    static displayName = 'Module.UserSettings.Privacy';
    static propTypes = {
        setCurrentView: PropTypes.func,
        getSettings: PropTypes.func,
        t: PropTypes.func,
        invalidateAllTrainingShareLinks: PropTypes.func,
        isInvalidateAllTrainingShareLinksSuccess: PropTypes.bool
    };

    componentDidMount() {
        this.props.setCurrentView();
        this.props.getSettings();
    }

    onValuesChange = (propsParent, propsItem, value) => {
        propsParent[propsItem] = value;
        this.props.updateSettings(this.props.settings);
    };

    getSessionsPrivacy() {
        return (
            <SessionsPrivacy
                isInvalidateAllTrainingShareLinksSuccess={this.props.isInvalidateAllTrainingShareLinksSuccess}
                invalidateAllTrainingShareLinks={this.props.invalidateAllTrainingShareLinks}/>
        );
    }

    getPolarClubCommunication() {
        const { settings } = this.props;
        return (
            <PolarClubCommunication
            value={settings ? settings.clubCommunication : false}
            onChange={(value) => this.onValuesChange(settings, 'clubCommunication', value)} />
        );
    }

    getTelemetry() {
        const { settings } = this.props;
        return (
            <Telemetry
                value={settings ? settings.telemetryDisabled : false}
                onChange={(value) => this.onValuesChange(settings, 'telemetryDisabled', value)} />
        );
    }

    getUserConsentSettings() {
        const { t } = this.props;
        const polarClubCommunication = this.getPolarClubCommunication();
        const sessionPrivacyAndClubCommunication = this.getSessionPrivacyAndClubCommunication();

        if (this.props.settings.userConsents) {
            var consentList = [];
            for (var i = 0; i < this.props.settings.userConsents.length; i++) {
                var consent = this.props.settings.userConsents[i];
                if (consent.type === AGE_CONSENT_TYPE) {
                    var birthdayMetaData = consent.metaData.filter(metaData => metaData.description === AGE_CONSENT_META_DATA_BIRTHDAY);
                    var birthdayText = birthdayMetaData[0].content;
                    if (birthdayText) {
                        var birthday = moment(birthdayText);
                        var age = moment().diff(birthday, 'years');
                        if (age >= AGE_CONSENT_FULL_AGE_LIMIT) {
                            continue; // Apparently we dont want to display guardian consent for overaged users in the settings
                        }
                    }
                }
                consentList.push(
                    <Consent
                        key={Math.random()}
                        title={consent.name}
                        description={consent.description}
                        isRequired={consent.required}
                        isChecked={consent.accepted}
                        onChange={this.onConsentValueChange(consent)}
                        type={consent.type}
                        url={consent.contentUrl} />
                );
            }

            var languageCode = i18n.language;
            var langParts = languageCode.split('-');
            if (langParts.length === 2) {
                var langPart = langParts[0].toLowerCase();
                var countryPart = langParts[1].toLowerCase();
                languageCode = countryPart + '-' + langPart;
            }
            const privacyUrl = 'https://www.polar.com/{{LANG}}/legal/privacy-notice'.replace('{{LANG}}', languageCode);
            const faqUrl = 'https://www.polar.com/{{LANG}}/legal/faq'.replace('{{LANG}}', languageCode);
            const accountUrl = 'https://account.polar.com';

            return (
                <div className="row">
                    <div className="col-sm-3 no-padding">
                        <h2>{t('register:terms.of.use')}</h2>
                        <p dangerouslySetInnerHTML={{__html: t('settings:help.privacy.content', { '0': '<a target="_blank" href="' + privacyUrl + '">' + t('gdpr:consents.popup.privacy_notice', 'Privacy Notice') + '</a>', '1': '<a target="_blank" href="' + faqUrl + '">' + t('settings:help.privacy.faq_link', 'FAQ') + '</a>' })}} />
                        <p dangerouslySetInnerHTML={{__html: t('gdpr:settings.consents.popup.manage_your_account', { '0': '<a target="_blank" href="' + accountUrl + '">' + accountUrl + '</a>'} ) }} />
                    </div>
                    <div className="col-sm-8">
                        {consentList}
                        {sessionPrivacyAndClubCommunication}
                    </div>
                </div>);
        } else {
            return null;
        }
    }

    getSessionPrivacyAndClubCommunication() {
        const { t, settings } = this.props;
        const sessionPrivacy = this.getSessionsPrivacy();
        const polarClubCommunication = this.getPolarClubCommunication();
        const telemetry = this.getTelemetry();

        return(
            <div>
                <br/>
                <br/>
                {sessionPrivacy}
                <hr/>
                {telemetry}
                {polarClubCommunication}
            </div>
        );
    }

    onConsentValueChange(consent) {
        return (value) => { this.onValuesChange(consent, 'accepted', value); };
    }

    render() {
        const { t, settings } = this.props;

        if (settings) {
            const userConsentSettings = this.getUserConsentSettings();

            return (
                <div className="container-fluid primary-heading-chapter privacy-settings">
                    {userConsentSettings}
                    <CancelSaveButton />
                </div>
            );
        }
        return null;
    }
}

PrivacyView.propTypes = {
    settings: PropTypes.object,
    updateSettings: PropTypes.func
};

const mapDispatchToProps = (dispatch) => {
    return {
        getSettings: () => dispatch(getSettings(PAGE_NAMES.PRIVACY)),
        updateSettings: (settings) => dispatch(updateSettings(PAGE_NAMES.PRIVACY, settings)),
        setCurrentView: () => dispatch(setCurrentView(PAGE_NAMES.PRIVACY)),
        invalidateAllTrainingShareLinks: () => dispatch(invalidateAllTrainingShareLinks())
    };
};

const mapStateToProps = state => {
    return {
        settings: state.settings.privacy,
        isInvalidateAllTrainingShareLinksSuccess: state.settings.isInvalidateAllTrainingShareLinksSuccess
    };
};

export default compose(connect(mapStateToProps, mapDispatchToProps), translate(['gdpr', 'settings'], { wait: true }))(PrivacyView);



// WEBPACK FOOTER //
// ./src/UserSettings/components/PrivacyView/index.jsx